import pc from "picocolors";
import {
  MCP_URL,
  SIGN_UP_URL,
  SIGN_IN_URL,
  CONSOLE_URL,
  ONBOARDING_URL,
} from "./urls.js";
import type { CarrierStatus } from "./status.js";

/** Natural-language prompts operators can paste into Claude / MCP clients. */
export const FLEET_NL_EXAMPLES: ReadonlyArray<{ label: string; prompt: string }> = [
  { label: "Fleet health", prompt: "Show my fleet health — accounts, eSIMs, low-balance alerts" },
  { label: "Subscriber lookup", prompt: "Look up subscriber ICCID 8944… and diagnose connectivity" },
  { label: "Usage anomalies", prompt: "Detect usage anomalies and burn-rate risks this week" },
  { label: "Issue from inventory", prompt: "Issue 3 free eSIMs from inventory on my account and assign the starter package" },
  { label: "Billing check", prompt: "What's my platform credit balance and Stripe Connect payout status?" },
  { label: "Wallet balance", prompt: "What's my managed prepaid wallet balance and auto-top-up status?" },
  { label: "Churn risk", prompt: "List high churn-risk subscribers with retention ideas" },
];

/** Zero-cred Entry A messaging shown after install / on home. */
export function oauthFirstUseNote(): string {
  return [
    "Entry A — zero credentials up front",
    `  MCP URL:  ${MCP_URL}`,
    "  Auth:     OAuth on first use (Clerk — Google, GitHub, or email)",
    "  Token:    none required in Claude / Cursor / Windsurf",
    "",
    "When you first talk to Carrier in your MCP client, the browser opens",
    "sign-in/sign-up. After authorize, tools work. Headless? Use an org API key",
    `(ak_…) from ${CONSOLE_URL} → Settings → API Keys.`,
  ].join("\n");
}

export function formatStatusBlock(st: CarrierStatus): string {
  const yn = (ok: boolean, yes = "yes", no = "no") => (ok ? pc.green(yes) : pc.yellow(no));
  const authLabel =
    st.authMode === "oauth-first-use"
      ? pc.cyan("OAuth on first use (no token needed)")
      : st.authMode === "api-key"
        ? pc.green("org API key in env (CARRIER_API_KEY)")
        : st.authMode === "ocs-token"
          ? pc.green("OCS token in env")
          : pc.yellow("not ready — install MCP or open sign-up");

  return [
    `Claude Code:   ${yn(st.claudeFound, "found", "not on PATH")}`,
    `Plugin:        ${st.pluginInstalled ? pc.green(`installed v${st.pluginVersion ?? "?"}`) : pc.yellow("not installed")}`,
    `MCP registered:${st.mcpRegistered ? pc.green(" yes") : pc.yellow(" no")}${st.mcpDetail ? pc.dim(`  (${st.mcpDetail})`) : ""}`,
    `MCP URL:       ${st.mcpUrlMatches ? pc.green(MCP_URL) : pc.yellow("not production / missing")}`,
    `Auth path:     ${authLabel}`,
    `Headless token:${st.tokenPresent ? pc.green(` ${st.tokenKind}`) : pc.dim(" none (optional)")}`,
  ].join("\n");
}

export function formatNextSteps(st: CarrierStatus): string {
  return st.nextSteps.map((s, i) => `  ${i + 1}. ${s}`).join("\n");
}

export function formatNlExamples(): string {
  return FLEET_NL_EXAMPLES.map((e) => `  • ${pc.bold(e.label)}: "${e.prompt}"`).join("\n");
}

export function accountLinksNote(): string {
  return [
    `Sign up:     ${SIGN_UP_URL}`,
    `Sign in:     ${SIGN_IN_URL}`,
    `Console:     ${CONSOLE_URL}`,
    `Onboarding:  ${ONBOARDING_URL}`,
    `MCP home:    https://mcp.carrier.llc`,
  ].join("\n");
}

/** Wrap a failure with recovery guidance — never leave the user with a raw error alone. */
export function withNextStep(errorMsg: string, next: string[]): string {
  return [errorMsg, "", "What to do next:", ...next.map((s) => `  → ${s}`)].join("\n");
}
